import React from 'react';
import { TextInput } from '../ui/TextInput';
import { Select } from '../ui/Select';
import { DocumentoInput } from '../ui/DocumentoInput';
import { MiembroFotoUpload } from '../MiembroFotoUpload';
import type { TipoDocumento } from '../../utils/rutValidator';
import { fechaISO, fechaNacimientoMinima } from '../../utils/dateHelpers';

// Acota el selector de fecha del navegador: una fecha de nacimiento no es futura ni de hace más de
// 120 años. Sin esto el campo acepta años de cinco dígitos y el error aparece recién al avanzar.
const NACIMIENTO_MAXIMO = fechaISO(new Date());
const NACIMIENTO_MINIMO = fechaNacimientoMinima();

export interface IdentidadValues {
  readonly nombres: string;
  readonly apellidos: string;
  readonly genero: string;
  readonly rut_pasaporte: string;
  readonly fecha_nacimiento: string;
}

interface MiembroFieldsIdentidadProps {
  readonly values: IdentidadValues;
  readonly onFieldChange: (field: keyof IdentidadValues, value: string) => void;
  readonly errors: Record<string, string>;
  readonly required?: boolean;
  readonly tipoDoc: TipoDocumento;
  readonly onTipoDocChange: (t: TipoDocumento) => void;
  readonly onFotoReady: (dataUri: string) => void;
  readonly onFotoClear: () => void;
  readonly fotoError?: string;
  readonly fotoPosition?: 'first' | 'last';
  readonly fotoUrl?: string;
  readonly fotoRequerida?: boolean;
  readonly avisoCompromiso?: React.ReactNode;
  readonly fechaNacimientoSibling?: React.ReactNode;
}

const GENERO_OPTIONS = [
  { value: 'Dama', label: 'Dama' },
  { value: 'Caballero', label: 'Caballero' },
];

export const MiembroFieldsIdentidad: React.FC<MiembroFieldsIdentidadProps> = ({
  values, onFieldChange, errors, required = false,
  tipoDoc, onTipoDocChange, onFotoReady, onFotoClear, fotoError,
  fotoPosition = 'last', fotoUrl, fotoRequerida = false, avisoCompromiso,
  fechaNacimientoSibling,
}) => {
  const foto = (
    <div className="space-y-3">
      <MiembroFotoUpload
        onFotoReady={onFotoReady}
        onClear={onFotoClear}
        error={fotoError}
        required={fotoRequerida}
        initialUrl={fotoUrl}
      />
      {avisoCompromiso}
    </div>
  );

  return (
    <div className="space-y-4">
      {fotoPosition === 'first' && foto}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput label="Nombres" value={values.nombres}
          onChange={(v) => onFieldChange('nombres', v)} error={errors.nombres} required={required} />
        <TextInput label="Apellidos" value={values.apellidos}
          onChange={(v) => onFieldChange('apellidos', v)} error={errors.apellidos} required={required} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <Select label="Género" value={values.genero}
          onChange={(v) => onFieldChange('genero', v)} error={errors.genero} required={required}
          placeholder="Selecciona..." options={GENERO_OPTIONS} />
        <DocumentoInput
          tipo={tipoDoc} onTipoChange={onTipoDocChange}
          value={values.rut_pasaporte} onChange={(v) => onFieldChange('rut_pasaporte', v)}
          error={errors.rut_pasaporte} required={required} />
      </div>
      {fechaNacimientoSibling ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Fecha de nacimiento" type="date" value={values.fecha_nacimiento}
            onChange={(v) => onFieldChange('fecha_nacimiento', v)}
            min={NACIMIENTO_MINIMO} max={NACIMIENTO_MAXIMO}
            error={errors.fecha_nacimiento} required={required} />
          {fechaNacimientoSibling}
        </div>
      ) : (
        <TextInput label="Fecha de nacimiento" type="date" value={values.fecha_nacimiento}
          onChange={(v) => onFieldChange('fecha_nacimiento', v)}
          min={NACIMIENTO_MINIMO} max={NACIMIENTO_MAXIMO}
          error={errors.fecha_nacimiento} required={required} />
      )}
      {fotoPosition === 'last' && foto}
    </div>
  );
};
